import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { AclAssetModulePermission } from '@y42/acl/models';
import { createAclPermissionDetail } from '@y42/acl/util/create-acl-permission-detail';
import { HeaderWithIconTemplate } from '@y42/ag-grid';
import { connectState } from '@y42/angular/util';
import { IListViewComponent, ListViewColDef, ListViewGridMessages } from '@y42/core/list-view';
import {
  DataTestShowDialogCellRendererComponent,
  DataTestShowDialogCellRendererParam,
} from '@y42/data-test/shared/cell-renderers';
import { FeatureTypeLabel, Noun, Y42FeatureToAclAssetModuleResourceType, Y42FeatureTypeToLabel } from '@y42/feature';
import { AssetModuleAclService } from '@y42/feature/acl';
import { dataTestStrokedIcon } from '@y42/feature/icons';
import {
  AbstractFeatureListViewService,
  getDatasourceListFrameworkComponents,
  getDatasourceListViewGridOptions,
} from '@y42/feature/shared/list-view';
import { Y42FeatureType } from '@y42/json-schema';
import { UserQuery } from '@y42/user/state';
import { GridOptions, ValueGetterParams } from 'ag-grid-community';
import { BehaviorSubject, map, of, switchMap } from 'rxjs';
import { DatasourceListViewData } from '../../state/datasource/datasource.model';
import { DatasourceListViewColumnDefsService } from './datasource-list-view.column-defs.service';

type DatasourceFeatureTypes = Y42FeatureType.Integration | Y42FeatureType.UiModel | Y42FeatureType.SqlModel;

@UntilDestroy()
@Component({
  selector: 'y42-datasource-list-view',
  templateUrl: './datasource-list-view.component.html',
  styleUrls: ['./datasource-list-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DatasourceListViewComponent implements IListViewComponent {
  constructor(
    private readonly userQuery: UserQuery,
    private readonly listViewService: AbstractFeatureListViewService,
    private readonly datasourceListViewColumnDefsService: DatasourceListViewColumnDefsService,
    private readonly assetModuleAclService: AssetModuleAclService,
  ) {}

  private readonly _id$ = new BehaviorSubject<DatasourceFeatureTypes | ''>('');
  @Input() set id(val: DatasourceFeatureTypes) {
    this._id$.next(val);
  }

  @Input() name = '';
  @Input() searchLabel = '';
  @Input() searchPlaceholder = '';

  readonly columnDefs: ListViewColDef[] = [
    ...Object.values(
      this.datasourceListViewColumnDefsService.columnDefs({
        locale: this.userQuery.getActiveUser()?.locale,
        disableSelection: true,
        toggleStarred: (id) => this.listViewService.toggleStarred(id),
      }),
    ),
    this.getDataTestColDef(),
  ];

  private readonly idToLabelMap: Record<DatasourceFeatureTypes | '', { label: FeatureTypeLabel; prefix: string }> = {
    '': { label: { [Noun.Singular]: 'item' }, prefix: 'an' },
    [Y42FeatureType.Integration]: { label: Y42FeatureTypeToLabel[Y42FeatureType.Integration], prefix: 'an' },
    [Y42FeatureType.UiModel]: { label: Y42FeatureTypeToLabel[Y42FeatureType.UiModel], prefix: 'a' },
    [Y42FeatureType.SqlModel]: { label: Y42FeatureTypeToLabel[Y42FeatureType.SqlModel], prefix: 'a' },
  };

  private readonly staticGridOptions: GridOptions = getDatasourceListViewGridOptions({
    importActiveRowClassRule: (_) => false,
    hideSubEntries: false,
    rowSelection: 'single',
  });

  readonly frameworkComponents: GridOptions['components'] = {
    ...getDatasourceListFrameworkComponents(),
    dataTest: DataTestShowDialogCellRendererComponent,
  };

  public readonly state = connectState(this, {
    id: this._id$,
    gridOptions: this._id$.pipe(
      map((id) => ({
        ...this.staticGridOptions,
        noRowsOverlayComponentParams: {
          text: `No ${this.idToLabelMap[id].label[Noun.Singular]} found`,
          body: 'Please try with another filter',
        },
      })),
    ),
    messages: this._id$.pipe(
      map((id) => {
        const { prefix, label } = this.idToLabelMap[id];
        const messages: ListViewGridMessages = {
          noDataHeader: `There are no ${label[Noun.Singular]}s`,
          noDataBody: `Please add your first ${label[Noun.Singular]}`,
          addButtonLabel: `Add ${prefix} ${label[Noun.Singular]}`,
          addPermissionAllowedButtonTooltip: `You do not have permission to create ${label[Noun.Plural]}`,
        };
        return messages;
      }),
    ),
    addPermissionAllowed: this._id$.pipe(
      switchMap((id) => {
        if (!id) {
          return of(createAclPermissionDetail({ allowed: false }));
        }

        const resourceType = Y42FeatureToAclAssetModuleResourceType[id];
        if (!resourceType) {
          return of(createAclPermissionDetail({ allowed: false }));
        }

        return this.assetModuleAclService.checkAssetModulePermission({
          resourceType,
          permission: AclAssetModulePermission.Create,
        });
      }),
    ),
  });

  /**
   * "Data Test" column is used in this view
   */
  private getDataTestColDef() {
    return {
      headerName: 'Data Tests',
      field: 'data_test_count',
      maxWidth: 130,
      minWidth: 100,
      order: 502,
      sortable: true,
      filter: 'agNumberColumnFilter',
      headerComponentParams: {
        template: HeaderWithIconTemplate(dataTestStrokedIcon),
        enableSorting: true,
      },
      cellRenderer: 'dataTest',
      valueGetter: (params: ValueGetterParams<DatasourceListViewData>): DataTestShowDialogCellRendererParam => {
        const result: DataTestShowDialogCellRendererParam = {
          dataTestCount:
            params.node && params.node.group
              ? params.node.allLeafChildren
                  .map((x) => x.data?.data_test_count ?? 0)
                  .reduce((partialSum, a) => partialSum + a, 0)
              : params.data?.data_test_count,
          $path: params.data?.child_$path,
        };
        return result;
      },
      comparator: (valueA: DataTestShowDialogCellRendererParam, valueB: DataTestShowDialogCellRendererParam) => {
        return (valueA?.dataTestCount ?? 0) - (valueB?.dataTestCount ?? 0);
      },
    };
  }
}
